import { computed, observable, makeObservable } from 'mobx';

import type { Nullable } from 'chaika-common-types';
import type { AvatarUnits } from 'chaika-frontend-types/frontend-cms';

import { PrimaryChannel } from 'chaika-enums';

import { assignParams } from '@utils/assignParams';
import { formatFullName } from '@utils/formatFullName';
import { formatShortFullName } from '@utils/formatShortFullName';

export interface PersonParams extends PersonNameParams {
    accountFirstName?: string;
    accountLastName?: string;
    accountPatronymic?: string;
    engFirstName?: string;
    engLastName?: string;
    engPatronymic?: string;
    engOldLastName?: string;
    profileUrl?: Nullable<string>;
    avatar?: string;
    avatarUnits?: AvatarUnits;
    telegram?: string;
    telegramName?: string;
    skype?: string;
    viber?: string;
    facebook?: string;
    whatsApp?: string;
    primaryChannel?: PrimaryChannel;
}

export interface PersonNameParams {
    firstName: string;
    lastName: string;
    oldLastName?: string;
    patronymic?: string;
}

export class Person {
    @observable public firstName: string = '';
    @observable public lastName: string = '';
    @observable public accountFirstName: string = '';
    @observable public accountLastName: string = '';
    @observable public oldLastName?: string;
    @observable public patronymic?: string;
    @observable public accountPatronymic?: string;
    @observable public engOldLastName?: string;
    @observable public engFirstName?: string;
    @observable public engLastName?: string;
    @observable public engPatronymic?: string;
    @observable public avatar?: string;
    @observable public profileUrl?: Nullable<string>;
    @observable public avatarUnits?: AvatarUnits;
    @observable public telegram?: string;
    @observable public telegramName?: string;
    @observable public skype?: string;
    @observable public viber?: string;
    @observable public facebook?: string;
    @observable public whatsApp?: string;
    @observable public primaryChannel?: Nullable<PrimaryChannel>;

    constructor(params: PersonParams = {} as PersonParams) {
        makeObservable(this);
        assignParams<any>(this, params);
    }

    @computed
    public get fullName(): string {
        return formatFullName({
            firstName: this.firstName,
            lastName: this.lastName,
            oldLastName: this.oldLastName,
            patronymic: this.patronymic,
        });
    }

    @computed
    public get engFullName(): string {
        const oldLastName = this.engOldLastName ? ` (${this.engOldLastName}) ` : ' ';
        return `${this.engLastName || ''}${oldLastName}${this.engFirstName || ''}${
            this.engPatronymic ? ` ${this.engPatronymic}` : ''
        }`;
    }

    @computed
    public get shortFullName(): string {
        return formatShortFullName(this);
    }

    @computed
    public get engShortFullName(): string {
        return `${this.engLastName || ''} ${this.engFirstName || ''}`;
    }

    @computed
    public get initials(): string {
        return `${this.lastName || ''}${this.getFirstLetter(this.firstName)}${
            this.patronymic ? this.getFirstLetter(this.patronymic) : ''
        }`;
    }

    public serialize(): PersonParams {
        return {
            firstName: this.firstName,
            lastName: this.lastName,
            oldLastName: this.oldLastName,
            patronymic: this.patronymic,
            accountFirstName: this.accountFirstName,
            accountLastName: this.accountLastName,
            accountPatronymic: this.accountPatronymic,
            profileUrl: this.profileUrl,
            engFirstName: this.engFirstName,
            engLastName: this.engLastName,
            engPatronymic: this.engPatronymic,
            engOldLastName: this.engOldLastName,
            avatar: this.avatar,
            avatarUnits: this.avatarUnits,
            telegram: this.telegram,
            telegramName: this.telegramName,
            skype: this.skype,
            viber: this.viber,
            facebook: this.facebook,
            whatsApp: this.whatsApp,
            primaryChannel: this.primaryChannel ?? undefined,
        };
    }

    protected getFirstLetter(name: string): string {
        return name ? ` ${name[0]}.` : '';
    }
}
